import styled from 'styled-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fonts } from '@/theme/fonts';
import OrderOption from './option/OrderOption';
import ToggleButton from '@/components/button/ToggleButton';
import { GetPartnerServiceOptionsResponse } from '@/api/type/types';
import { useDispatch } from 'react-redux';
import { service } from '@/stores/slices/service-regist/serviceSlices';
import { useLoading } from '@/context/LoadingContext';
import { useAppSelector } from '@/stores/hooks';
import { putServiceDeActivate } from '@/api/service-regist/putServiceDeActivate';
import { useAlert } from '@/hooks/useAlert';
import { useTranslation } from 'react-i18next';
import { SERVICE_TYPE } from '@/api/type/commonTypes';

interface OrderCategoryProps {
  imgUrl: string;
  data: GetPartnerServiceOptionsResponse | undefined; // 서비스 등록 x -> undefined
  title: string;
  serviceType: SERVICE_TYPE;
  desc: string;
}
const OrderCategory = ({
  imgUrl,
  data,
  title,
  serviceType,
  desc,
}: OrderCategoryProps) => {
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = useLoading();
  const { putPartnerServiceOptions, partnerServiceOptions } = useAppSelector(
    (state) => state.service
  );
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  /** 비활성화 */
  const onClickDeActiveToggle = useCallback(async () => {
    console.log('비활성화');
    if (!data) return;
    console.log('putPartnerServiceOptions', putPartnerServiceOptions);
    const targetOption = putPartnerServiceOptions?.[serviceType];
    if (targetOption && data.serviceType !== serviceType) {
      showAlert({
        message: t('chooseTheServiceToProvideToThePlayer'),
      });
      return;
    }
    startLoading();
    try {
      const res = await putServiceDeActivate({ partnerServiceId: data.id });
      if (res.isSuccess) {
        dispatch(
          service.setPartnerServiceOptions(
            partnerServiceOptions.map((item) =>
              item.serviceType === data.serviceType
                ? { ...item, active: false }
                : item
            )
          )
        );
        dispatch(
          service.setPutPartnerServiceOptions({
            serviceType,
            data: null,
          })
        );
      }
    } finally {
      stopLoading();
    }
  }, [
    data,
    dispatch,
    partnerServiceOptions,
    putPartnerServiceOptions,
    serviceType,
    showAlert,
    startLoading,
    stopLoading,
    t,
  ]);

  /** ON 버튼 동작: 활성화 X, 저장 눌러야 활성화 진행 */
  const onClickOnOpen = useCallback(() => {
    console.log('활성화');
    if (!data) return;
    const currentPutData = putPartnerServiceOptions?.[serviceType];
    dispatch(
      service.setPutPartnerServiceOptions({
        serviceType,
        data: currentPutData ? null : data,
      })
    );
  }, [data, dispatch, putPartnerServiceOptions, serviceType]);

  const isOpen = useMemo(() => {
    if (!data) return false;
    const isEditingThis = putPartnerServiceOptions[serviceType]?.id === data.id;
    return isEditingThis || data.active;
  }, [data, putPartnerServiceOptions, serviceType]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasData = Object.values(putPartnerServiceOptions).some(
        (value) => value !== null && value !== undefined
      );

      if (!hasData) return;

      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [putPartnerServiceOptions]);

  return (
    <Container>
      <CategoryWrap data-disabled={data ? false : true} data-ison={isOpen}>
        <div>{title}</div>
        <ToggleButton
          ison={isOpen}
          onClickToggle={data?.active ? onClickDeActiveToggle : onClickOnOpen}
          disabled={data ? false : true}
        />
      </CategoryWrap>
      {isOpen && data && (
        <OrderOption
          data={data}
          desc={desc}
          serviceType={serviceType}
          imgUrl={imgUrl}
        />
      )}
    </Container>
  );
};

export default OrderCategory;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 876px;
`;

const CategoryWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 51px;
  padding: 0 45px 0 16px;
  background: #f8f8f8;
  border-radius: 8px 8px 0 0;
  color: #151131;
  font-size: 30px;
  line-height: 120%;
  font-weight: ${fonts.weight.semiBold};
  &[data-disabled='true'] {
    opacity: 0.5;
  }
  &[data-ison='true'] {
    background: #769bf7;
    color: #ffffff;
  }
`;
